// Product-level gallery (responsive-capable container provides layout).
// Pure presentational: no design system, inline styles minimal & semantic.

import React from 'react';
// ⬇️ Use the adapter instead of FavoriteList
import FavoriteListAdapter from '../../app/adapters/FavoriteListAdapter';

export type ProductLite = {
  productID: string;
  title: string;
  price?: string;
  thumbnailUrl?: string;
  owned?: boolean;          // from backend (/products when logged in)
  publicCount?: number;
  privateCount?: number;
};

export type FileFav = {
  full?: boolean;
  timestamps?: number[];
};

export type FileFavoritesMap = Record<string, FileFav>;

type Props = {
  products: ProductLite[];
  productFavoritesSet?: Set<string>;
  fileFavorites?: FileFavoritesMap;
  onOpenProduct: (productID: string) => void;
  onToggleProductFavorite?: (productID: string) => void;
  onJumpToTimestamp?: (fileId: string, seconds: number) => void;
  isLoading?: boolean;
  /** Optional class hooks so the app theme can style things */
  className?: string;
  classes?: {
    grid?: string;
    loading?: string;
    empty?: string;
    card?: string;
    thumbWrap?: string;
    thumbImg?: string;
    thumbFallback?: string;
    ribbonOwned?: string;
    lockOverlay?: string;
    lockIcon?: string;
    favBtn?: string;
    cardBody?: string;
    titleRow?: string;
    title?: string;
    metaRow?: string;
    priceChip?: string;
    ownedChip?: string;
    favListWrap?: string;
  };
};

const MediaGallery: React.FC<Props> = ({
  products = [],
  productFavoritesSet,
  fileFavorites = {},
  onOpenProduct,
  onToggleProductFavorite,
  onJumpToTimestamp,
  isLoading = false,
  className,
  classes = {},
}) => {
  // Unify signature for the adapter (accepts number | string)
  const onJumpUnified = React.useCallback(
    (fileId: string, seconds: number | string) => {
      if (!onJumpToTimestamp) return;
      const n = typeof seconds === 'number' ? seconds : parseInt(String(seconds), 10) || 0;
      onJumpToTimestamp(fileId, Math.max(0, n));
    },
    [onJumpToTimestamp]
  );

  return (
    <div className={className}>
      {isLoading ? <div className={classes.loading} role="status">Loading…</div> : null}

      <div className={classes.grid} role="list">
        {products.map((p) => {
          const perFileFavs = collectProductFileFavorites(p.productID, fileFavorites);

          return (
            <div key={p.productID} role="listitem">
              <ProductCard
                product={p}
                isFavorited={!!productFavoritesSet?.has(p.productID)}
                onOpen={() => onOpenProduct?.(p.productID)}
                onToggleFavorite={
                  onToggleProductFavorite
                    ? () => onToggleProductFavorite(p.productID)
                    : undefined
                }
                classes={classes}
              />

              {/* Compact “moments” under the tile */}
              {perFileFavs.length > 0 && onJumpToTimestamp && (
                <div className={classes.favListWrap}>
                  {perFileFavs.map(({ fileId, fav }) => (
                    <FavoriteListAdapter
                      key={fileId}
                      fav={fav}
                      compact
                      title="Your moments"
                      fileId={fileId}
                      onJumpToTimestamp={onJumpUnified}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!isLoading && products.length === 0 ? (
        <p className={classes.empty}>No products to show yet.</p>
      ) : null}
    </div>
  );
};

/* ----------------------------- Helpers ----------------------------------- */
/** Group fileFavorites entries belonging to productID (fileId starts with `${productId}:`). */
function collectProductFileFavorites(productId: string, fileFavorites: FileFavoritesMap) {
  const out: Array<{ fileId: string; fav: Required<FileFav> }> = [];
  for (const [fileId, fav] of Object.entries(fileFavorites || {})) {
    if (!fileId.startsWith(`${productId}:`)) continue;
    const timestamps = Array.isArray(fav?.timestamps)
      ? fav.timestamps
          .map((n) => parseInt(String(n), 10))
          .filter((n) => Number.isFinite(n) && n >= 0)
          .sort((a, b) => a - b)
      : [];
    const full = !!fav?.full;
    if (timestamps.length || full) {
      out.push({ fileId, fav: { full, timestamps } });
    }
  }
  return out;
}

/* --------------------------------- Card ---------------------------------- */

type CardProps = {
  product: ProductLite;
  isFavorited: boolean;
  onOpen: () => void;
  onToggleFavorite?: () => void;
  classes: NonNullable<Props['classes']>;
};

const ProductCard: React.FC<CardProps> = ({
  product,
  isFavorited,
  onOpen,
  onToggleFavorite,
  classes,
}) => {
  const owned = !!product.owned;
  const priceLabel = product.price || '';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen();
      }}
      className={classes.card}
      aria-label={product.title}
    >
      <div className={classes.thumbWrap}>
        {product.thumbnailUrl ? (
          <img
            src={product.thumbnailUrl}
            alt={product.title}
            loading="lazy"
            decoding="async"
            className={classes.thumbImg}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className={classes.thumbFallback} aria-hidden>
            {(product.title || '').slice(0, 1).toUpperCase() || '🖼️'}
          </div>
        )}

        {/* Owned ribbon / Lock overlay */}
        {owned ? (
          <div className={classes.ribbonOwned}>Owned</div>
        ) : (
          <div className={classes.lockOverlay}>
            <span className={classes.lockIcon} aria-hidden>
              🔒
            </span>
          </div>
        )}

        {/* Product-level favorite (read/write if handler present) */}
        {onToggleFavorite ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite();
            }}
            aria-pressed={isFavorited}
            aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            title={isFavorited ? 'Favorited' : 'Add to favorites'}
            className={classes.favBtn}
          >
            {isFavorited ? '❤️' : '🤍'}
          </button>
        ) : null}
      </div>

      <div className={classes.cardBody}>
        <div className={classes.titleRow}>
          <h3 className={classes.title} title={product.title}>
            {product.title}
          </h3>
        </div>

        <div className={classes.metaRow}>
          {!owned && priceLabel ? <span className={classes.priceChip}>{priceLabel}</span> : null}
          {owned ? <span className={classes.ownedChip}>Unlocked</span> : null}
        </div>
      </div>
    </div>
  );
};

export default MediaGallery;
